import { useFormik } from 'formik';
import { useShallow } from 'zustand/react/shallow';
import Stack from '@mui/material/Stack';
import { phoneNumberSchema } from '@components/Checkout/steps/PhoneNumberStep/utils/phoneNumberStepSchema';
import { INITIAL_VALUES } from '@components/Checkout/steps/PhoneNumberStep/utils/phoneNumberStepUtils';
import { ChoiceSelector } from '@components/Checkout/steps/PhoneNumberStep/ChoiceSelector';
import { DirectoryListing } from '@components/Checkout/steps/PhoneNumberStep/DirectoryListing';
import type { ActiveFormProps } from '@components/Checkout/steps/PhoneNumberStep/PhoneNumberStep.types';
import { PortInFields } from '@components/Checkout/steps/PhoneNumberStep/PortInFields';
import { useRemovePhoneBill } from '@components/Checkout/hooks/useRemovePhoneBill';
import { useShoppingCart } from '@hooks/useShoppingCart/useShoppingCart';
import { useAnalytics } from '@hooks/useAnalytics/useAnalytics';
import { selectQualifiedAddressId, useQualificationFlowStore } from '@store/qualificationStore/qualificationStore';
import LoadingButton from '@shared/components/buttons/LoadingButton/LoadingButton';
import type { PhoneFormValues } from '@t/checkout.types';

export const ActiveForm = ({
  sessionId,
  directoryListings,
  phoneProviders,
  savedValues,
  isSaving,
  onSave,
}: ActiveFormProps) => {
  const { cart } = useShoppingCart(sessionId ?? '');
  const { resolvedAddressId, qualifiedAddressId } = useQualificationFlowStore(
    useShallow((s) => ({
      resolvedAddressId: s.resolvedAddressId,
      qualifiedAddressId: selectQualifiedAddressId(s),
    }))
  );
  const analytics = useAnalytics();

  const formik = useFormik<PhoneFormValues>({
    initialValues: savedValues ?? INITIAL_VALUES,
    enableReinitialize: true,
    validationSchema: phoneNumberSchema,
    onSubmit: (values) => {
      if (sessionId && cart) {
        analytics.fire('trackPhoneOptionSaved', {
          sessionId,
          cart,
          qualification: { qualifiedAddressId, resolvedAddressId },
          choice: values.choice,
        });
      }
      onSave(values);
    },
  });

  const { values, setFieldValue, setFieldTouched, handleSubmit } = formik;

  const { remove: removeSavedFile } = useRemovePhoneBill(sessionId);

  const handleRemoveSavedFile = (fileId: string): void => {
    removeSavedFile(fileId, {
      onSuccess: () => {
        setFieldValue(
          'existingPhoneBillFiles',
          values.existingPhoneBillFiles.filter((entry) => entry.fileId !== fileId)
        );
      },
    });
  };

  const handleChoiceChange = (choice: PhoneFormValues['choice']) => {
    setFieldValue('choice', choice);
    setFieldTouched('choice', true, false);
  };

  const handleDirectoryChange = (value: string) => {
    setFieldValue('directoryListing', value);
    setFieldTouched('directoryListing', true, false);
  };

  return (
    <Stack gap="30px">
      <ChoiceSelector value={values.choice} onChange={handleChoiceChange} />

      {values.choice === 'NEWNUMBER' && (
        <DirectoryListing
          value={values.directoryListing}
          options={directoryListings}
          headingVariant="roboto-600-14"
          onChange={handleDirectoryChange}
        />
      )}

      {values.choice === 'PORTIN' && (
        <PortInFields
          formik={formik}
          phoneProviders={phoneProviders}
          directoryListings={directoryListings}
          onRemoveSavedFile={handleRemoveSavedFile}
        />
      )}

      <LoadingButton variant="contained" onClick={() => handleSubmit()} loading={isSaving}>
        Save and Continue
      </LoadingButton>
    </Stack>
  );
};
